/**
 * BATCH 23 — PART 19: Workflows 299-305
 * Data/AI final 7 workflows
 * Services: Ahrefs, HubSpot, Clearbit, Gong, OpenAI, Todoist, Slack, Google Sheets
 * 8 nodes each, IFTTT/Zapier style cross-platform automations
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 299-305 ============

export const ELITE_BATCH23_PART19: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 299. SEO Competitive Analyzer | Data/AI | Pattern K ──
  "SEO Competitive Analyzer": {
    nodes: [
      trigger(
        "Webhook: Competitor URL submitted for SEO analysis",
        "webhook_app",
        {
          source: "internal",
          event: "competitor_seo_request",
          howItStarts:
            "Fires when a team member submits a competitor URL for SEO competitive analysis via webhook or internal tool.",
          setup:
            "Configure webhook to receive competitor_url, our_domain, target_keywords, and analysis_scope (full/keywords/backlinks) in the payload.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Ahrefs: Fetch competitor domain metrics and rankings",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://apiv2.ahrefs.com?token={ahrefs_api_key}&from=domain_rating&target={competitor_url}&mode=domain&output=json",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "domain_rating",
                customName: "competitor_dr",
                type: "number",
              },
              {
                name: "ahrefs_rank",
                customName: "competitor_rank",
                type: "number",
              },
              {
                name: "backlinks",
                customName: "competitor_backlinks",
                type: "number",
              },
              {
                name: "referring_domains",
                customName: "competitor_ref_domains",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze competitor SEO vs our domain and identify gaps",
        520,
        "You are an expert SEO strategist. Compare competitor and our domain metrics. Identify keyword gaps, backlink opportunities, and content areas where competitor outranks us. Return JSON with fields: competitor_domain, our_domain, dr_gap, keyword_gaps (top 5 comma-separated), backlink_gap_count, content_opportunities (top 3 comma-separated), priority_action, overall_threat_level (high/medium/low).",
        "Competitor: {competitor_url}, DR: {competitor_dr}, Rank: {competitor_rank}, Backlinks: {competitor_backlinks}, Ref domains: {competitor_ref_domains}. Our domain: {our_domain}. Target keywords: {target_keywords}",
        [
          { field: "dr_gap", as: "dr_gap", type: "number" },
          { field: "keyword_gaps", as: "keyword_gaps", type: "string" },
          {
            field: "backlink_gap_count",
            as: "backlink_gap_count",
            type: "number",
          },
          {
            field: "content_opportunities",
            as: "content_opportunities",
            type: "string",
          },
          { field: "priority_action", as: "priority_action", type: "string" },
          {
            field: "overall_threat_level",
            as: "overall_threat_level",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "runCode",
        "Compute gap severity score and prioritize actions",
        760,
        {
          runCodeDetails: {
            code: "const drWeight = Math.min(dr_gap * 2, 40); const blWeight = Math.min(backlink_gap_count / 100, 30); const kwWeight = keyword_gaps.split(',').length * 6; const gap_score = Math.round(drWeight + blWeight + kwWeight); return { gap_score, severity: gap_score > 70 ? 'critical' : gap_score > 40 ? 'moderate' : 'minor', report_title: `SEO Gap: ${competitor_url} vs ${our_domain} — Score ${gap_score}` };",
            variables: [
              { name: "gap_score", customName: "gap_score", type: "number" },
              { name: "severity", customName: "gap_severity", type: "string" },
              {
                name: "report_title",
                customName: "report_title",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "5",
        "Google Sheets: Log competitive analysis results",
        1000,
        "SEO_Competitive",
        '[["{competitor_url}","{competitor_dr}","{competitor_backlinks}","{competitor_ref_domains}","{dr_gap}","{keyword_gaps}","{content_opportunities}","{gap_score}","{gap_severity}","{priority_action}"]]',
      ),
      slackNode(
        "6",
        "Slack: Post SEO gap highlights to #seo-strategy",
        1240,
        "📊 *SEO Competitive Analysis: {competitor_url}*\nThreat level: {overall_threat_level} | Gap score: {gap_score}/100\n\n*Keyword gaps:* {keyword_gaps}\n*Content opportunities:* {content_opportunities}\n*Backlink gap:* {backlink_gap_count} referring domains\n*Priority action:* {priority_action}",
      ),
      emailNode(
        "7",
        "Email: Send detailed SEO strategy brief to content team",
        1480,
        "{content_team_email}",
        "SEO Competitive Analysis: {competitor_url} — {gap_severity} gap detected",
        "<h2>{report_title}</h2><p><strong>Competitor DR:</strong> {competitor_dr} | <strong>DR Gap:</strong> {dr_gap}</p><p><strong>Keyword Gaps:</strong> {keyword_gaps}</p><p><strong>Content Opportunities:</strong> {content_opportunities}</p><p><strong>Backlink Gap:</strong> {backlink_gap_count} referring domains behind</p><p><strong>Recommended Priority Action:</strong> {priority_action}</p><p>Full data logged to the SEO Competitive Analysis sheet.</p>",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Append to competitive audit trail",
        1720,
        "Audit_Trail",
        '[["{timestamp}","seo_competitive_analysis","{competitor_url}","{gap_severity}","{gap_score}","{priority_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Performs on-demand SEO competitive analysis by pulling Ahrefs metrics for any competitor URL, using AI to identify keyword gaps and content opportunities, scoring the overall threat level, and distributing findings via Slack and email. Results are logged to Google Sheets for long-term competitive tracking and strategy planning.",
  },

  // ── 300. AI Discoverability / GEO Audit | Data/AI | Pattern L ──
  "AI Discoverability / GEO Audit": {
    nodes: [
      trigger(
        "Webhook: Brand or product submitted for AI visibility audit",
        "webhook_app",
        {
          source: "internal",
          event: "ai_discoverability_audit",
          howItStarts:
            "Fires when a team member submits a brand name or product for auditing how it appears across AI platforms (ChatGPT, Gemini, Perplexity, etc.).",
          setup:
            "Configure webhook to receive brand_name, product_name, target_queries (comma-separated), and competitor_brands in the payload.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Search across LLM platforms and assess brand visibility",
        280,
        "You are an AI discoverability auditor. Simulate searching for the given brand/product across major AI assistants. Assess how prominently the brand appears in typical AI-generated answers. Return JSON with fields: brand_name, queries_tested (number), mentioned_count, not_mentioned_count, competitor_mentions, visibility_score (0-100), positioning_summary, top_gaps (comma-separated queries where brand is missing).",
        "Brand: {brand_name}, Product: {product_name}, Target queries: {target_queries}, Competitors: {competitor_brands}",
        [
          { field: "brand_name", as: "brand_name", type: "string" },
          { field: "queries_tested", as: "queries_tested", type: "number" },
          { field: "mentioned_count", as: "mentioned_count", type: "number" },
          { field: "visibility_score", as: "visibility_score", type: "number" },
          {
            field: "positioning_summary",
            as: "positioning_summary",
            type: "string",
          },
          { field: "top_gaps", as: "top_gaps", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Score overall AI visibility and compute improvement targets",
        520,
        {
          runCodeDetails: {
            code: "const mention_rate = Math.round((mentioned_count / queries_tested) * 100); const needs_improvement = visibility_score < 50; const target_score = Math.min(visibility_score + 25, 100); return { mention_rate, needs_improvement, target_score, audit_grade: visibility_score >= 75 ? 'A' : visibility_score >= 50 ? 'B' : visibility_score >= 25 ? 'C' : 'D' };",
            variables: [
              {
                name: "mention_rate",
                customName: "mention_rate",
                type: "number",
              },
              {
                name: "needs_improvement",
                customName: "needs_improvement",
                type: "boolean",
              },
              {
                name: "audit_grade",
                customName: "audit_grade",
                type: "string",
              },
              {
                name: "target_score",
                customName: "target_score",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "AI: Generate actionable GEO improvement recommendations",
        760,
        "You are a generative engine optimization (GEO) strategist. Based on the audit results, generate specific recommendations to improve AI discoverability. Return JSON with fields: recommendation_1, recommendation_2, recommendation_3, quick_wins (comma-separated), estimated_timeline, priority_level (critical/high/medium).",
        "Brand: {brand_name}, Visibility score: {visibility_score}/100, Grade: {audit_grade}, Mention rate: {mention_rate}%, Gaps: {top_gaps}, Positioning: {positioning_summary}",
        [
          { field: "recommendation_1", as: "recommendation_1", type: "string" },
          { field: "recommendation_2", as: "recommendation_2", type: "string" },
          { field: "recommendation_3", as: "recommendation_3", type: "string" },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "priority_level", as: "priority_level", type: "string" },
        ],
      ),
      n("5", "condition", "Low AI visibility detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "visibility_score", operator: "less_than", value: "50" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert SEO team about low AI visibility",
        1240,
        "🔍 *AI Discoverability Audit: {brand_name}*\nGrade: {audit_grade} | Score: {visibility_score}/100 | Mention rate: {mention_rate}%\n\n*Top gaps:* {top_gaps}\n*Priority:* {priority_level}\n\n*Recommendations:*\n1. {recommendation_1}\n2. {recommendation_2}\n3. {recommendation_3}\n\n*Quick wins:* {quick_wins}",
      ),
      emailNode(
        "7",
        "Email: Send GEO audit report to marketing leadership",
        1480,
        "{marketing_lead_email}",
        "AI Discoverability Audit: {brand_name} — Grade {audit_grade} ({visibility_score}/100)",
        "<h2>AI Discoverability Audit: {brand_name}</h2><p><strong>Visibility Score:</strong> {visibility_score}/100 (Grade {audit_grade})</p><p><strong>Mention Rate:</strong> {mention_rate}% of tested queries</p><p><strong>Positioning:</strong> {positioning_summary}</p><h3>Gap Areas</h3><p>{top_gaps}</p><h3>Recommendations</h3><ol><li>{recommendation_1}</li><li>{recommendation_2}</li><li>{recommendation_3}</li></ol><p><strong>Quick Wins:</strong> {quick_wins}</p><p><strong>Target:</strong> Improve from {visibility_score} to {target_score} within {estimated_timeline}</p>",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log AI discoverability audit results",
        1720,
        "GEO_Audits",
        '[["{timestamp}","{brand_name}","{visibility_score}","{audit_grade}","{mention_rate}","{top_gaps}","{priority_level}","{recommendation_1}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Audits how a brand or product appears across AI platforms by simulating common queries, scoring visibility, and generating actionable GEO (generative engine optimization) recommendations. Alerts the SEO team when visibility is low and delivers a comprehensive audit report to marketing leadership. Results are tracked over time in Google Sheets.",
  },

  // ── 301. AI Cold Call Script Generator | Data/AI | Pattern K ──
  "AI Cold Call Script Generator": {
    nodes: [
      trigger(
        "Webhook: Prospect submitted for personalized cold call script",
        "webhook_app",
        {
          source: "internal",
          event: "cold_call_script_request",
          howItStarts:
            "Fires when a sales rep submits a prospect's details for an AI-generated personalized cold call script with objection handlers.",
          setup:
            "Configure webhook to receive prospect_name, prospect_title, company_name, company_domain, industry, and call_objective in the payload.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch prospect engagement history and deal context",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"company","operator":"EQ","value":"{company_name}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","lifecyclestage","hs_lead_status","notes_last_updated","num_associated_deals"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "results[0].properties.hs_lead_status",
                customName: "lead_status",
                type: "string",
              },
              {
                name: "results[0].properties.num_associated_deals",
                customName: "existing_deals",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Clearbit: Enrich company data for personalization context",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              {
                name: "name",
                customName: "enriched_company_name",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "company_size",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "est_revenue",
                type: "string",
              },
              { name: "tech", customName: "tech_stack", type: "string" },
              {
                name: "category.industry",
                customName: "enriched_industry",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "AI: Generate personalized cold call script with objection handlers",
        760,
        "You are an elite B2B sales script writer. Create a personalized cold call script based on the prospect profile and company context. Include an opening hook, value proposition, 3 common objection handlers, and a strong close. Return JSON with fields: opening_hook, value_proposition, objection_1_response, objection_2_response, objection_3_response, closing_cta, talk_track_notes, estimated_call_duration_min.",
        "Prospect: {prospect_name}, Title: {prospect_title}, Company: {enriched_company_name}, Industry: {enriched_industry}, Size: {company_size} employees, Revenue: {est_revenue}, Tech: {tech_stack}, Lifecycle: {lifecycle_stage}, Objective: {call_objective}",
        [
          { field: "opening_hook", as: "opening_hook", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "objection_1_response",
            as: "objection_1_response",
            type: "string",
          },
          {
            field: "objection_2_response",
            as: "objection_2_response",
            type: "string",
          },
          {
            field: "objection_3_response",
            as: "objection_3_response",
            type: "string",
          },
          { field: "closing_cta", as: "closing_cta", type: "string" },
          { field: "talk_track_notes", as: "talk_track_notes", type: "string" },
        ],
      ),
      n(
        "5",
        "runCode",
        "Add objection handler framework and format script",
        1000,
        {
          runCodeDetails: {
            code: "const script = `OPENING: ${opening_hook}\\n\\nVALUE PROP: ${value_proposition}\\n\\nOBJECTION HANDLERS:\\n1. ${objection_1_response}\\n2. ${objection_2_response}\\n3. ${objection_3_response}\\n\\nCLOSE: ${closing_cta}\\n\\nNOTES: ${talk_track_notes}`; const is_enterprise = company_size > 500; return { full_script: script, is_enterprise, script_label: `${prospect_name} @ ${enriched_company_name}` };",
            variables: [
              {
                name: "full_script",
                customName: "full_script",
                type: "string",
              },
              {
                name: "is_enterprise",
                customName: "is_enterprise",
                type: "boolean",
              },
              {
                name: "script_label",
                customName: "script_label",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "6",
        "condition",
        "Enterprise prospect requiring manager approval?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { field: "is_enterprise", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n(
        "7",
        "approvalGate",
        "Manager: Review enterprise cold call script",
        1480,
        {
          approvalGateDetails: {
            approverEmail: "{sales_manager_email}",
            message:
              "Enterprise cold call script for {script_label} needs your review before delivery to rep.\n\nScript preview:\n{opening_hook}\n\nApprove to deliver to the sales rep.",
          },
        },
      ),
      emailNode(
        "8",
        "Email: Deliver approved cold call script to sales rep",
        1720,
        "{rep_email}",
        "Cold Call Script Ready: {prospect_name} at {enriched_company_name}",
        "<h2>Cold Call Script: {script_label}</h2><h3>Opening Hook</h3><p>{opening_hook}</p><h3>Value Proposition</h3><p>{value_proposition}</p><h3>Objection Handlers</h3><ol><li>{objection_1_response}</li><li>{objection_2_response}</li><li>{objection_3_response}</li></ol><h3>Close</h3><p>{closing_cta}</p><h3>Talk Track Notes</h3><p>{talk_track_notes}</p><p><em>Company: {enriched_company_name} | {company_size} employees | {enriched_industry} | {est_revenue}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Generates personalized cold call scripts by enriching prospect data from HubSpot and Clearbit, then using AI to craft an opening hook, value proposition, three objection handlers, and a closing CTA. Enterprise prospects route through manager approval before delivery. Scripts are emailed directly to the rep with full context for immediate use.",
  },

  // ── 302. School Email Action-Item Extractor | Data/AI | Pattern I ──
  "School Email Action-Item Extractor": {
    nodes: [
      trigger("Email: School or teacher email received", "email", {
        source: "gmail",
        event: "school_email_received",
        howItStarts:
          "Fires when an email from a school, teacher, or school district address is received in the connected inbox (filtered by sender domain).",
        setup:
          "Connect Gmail and set up a filter for school-related sender domains (e.g., @schooldistrict.edu, @teacher.school.org). Emails matching the filter trigger this workflow.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract dates, events, action items, and deadlines from school email",
        280,
        "You are a parent assistant specializing in extracting actionable information from school communications. Parse the email and identify every date, event, action item, and deadline. Return JSON with fields: email_subject, school_name, events (comma-separated with dates), action_items (comma-separated), deadlines (comma-separated with dates), requires_response (true/false), urgency (high/medium/low), parent_summary.",
        "School email content: {email_body}, Subject: {email_subject}, From: {sender_email}",
        [
          { field: "school_name", as: "school_name", type: "string" },
          { field: "events", as: "extracted_events", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "deadlines", as: "deadlines", type: "string" },
          {
            field: "requires_response",
            as: "requires_response",
            type: "boolean",
          },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "parent_summary", as: "parent_summary", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Categorize items by type and parse calendar-ready dates",
        520,
        {
          runCodeDetails: {
            code: "const eventList = extracted_events ? extracted_events.split(',').map(e => e.trim()) : []; const taskList = action_items ? action_items.split(',').map(t => t.trim()) : []; const has_events = eventList.length > 0; const has_tasks = taskList.length > 0; const total_items = eventList.length + taskList.length; return { has_events, has_tasks, total_items, event_count: eventList.length, task_count: taskList.length };",
            variables: [
              { name: "has_events", customName: "has_events", type: "boolean" },
              { name: "has_tasks", customName: "has_tasks", type: "boolean" },
              {
                name: "total_items",
                customName: "total_items",
                type: "number",
              },
              {
                name: "event_count",
                customName: "event_count",
                type: "number",
              },
              { name: "task_count", customName: "task_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Calendar: Create events for school dates and deadlines",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/{school_calendar_id}/events",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "summary", value: "[School] {extracted_events}" },
              {
                key: "description",
                value: "From {school_name}: {parent_summary}",
              },
              { key: "source", value: '{"title":"School Email","url":""}' },
            ],
            variables: [
              { name: "id", customName: "calendar_event_id", type: "string" },
              { name: "htmlLink", customName: "calendar_link", type: "string" },
            ],
          },
        },
      ),
      n("5", "hitApi", "Todoist: Create tasks for parent action items", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.todoist.com/rest/v2/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {todoist_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "content", value: "[School] {action_items}" },
            {
              key: "description",
              value:
                "From {school_name}: {parent_summary}. Deadlines: {deadlines}",
            },
            { key: "project_id", value: "{school_project_id}" },
            { key: "priority", value: "3" },
          ],
          variables: [
            { name: "id", customName: "todoist_task_id", type: "string" },
            { name: "url", customName: "todoist_task_url", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send organized parent summary digest",
        1240,
        "{parent_email}",
        "School Update: {school_name} — {total_items} items extracted",
        "<h2>School Communication Summary</h2><p><strong>From:</strong> {school_name}</p><p><strong>Summary:</strong> {parent_summary}</p><h3>Events ({event_count})</h3><p>{extracted_events}</p><h3>Action Items ({task_count})</h3><p>{action_items}</p><h3>Deadlines</h3><p>{deadlines}</p><p><em>Calendar events and Todoist tasks created automatically. Urgency: {urgency}</em></p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log extracted school communication data",
        1480,
        "School_Comms",
        '[["{timestamp}","{school_name}","{email_subject}","{event_count}","{task_count}","{urgency}","{requires_response}","{parent_summary}"]]',
      ),
      slackNode(
        "8",
        "Slack: Notify family channel about school update",
        1720,
        "🏫 *School Update: {school_name}*\n{parent_summary}\n\n📅 Events: {event_count} | ✅ Tasks: {task_count} | Urgency: {urgency}\n\nCalendar events and tasks created automatically.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically processes incoming school emails by extracting dates, events, action items, and deadlines using AI. Creates Google Calendar events for school dates and Todoist tasks for parent action items. Sends an organized digest to parents and notifies the family Slack channel, ensuring nothing from school communications falls through the cracks.",
  },

  // ── 303. Top Relevant Website Pages Finder | Data/AI | Pattern K ──
  "Top Relevant Website Pages Finder": {
    nodes: [
      trigger(
        "Webhook: Domain submitted for top pages analysis",
        "webhook_app",
        {
          source: "internal",
          event: "top_pages_request",
          howItStarts:
            "Fires when a team member submits a domain for analysis to find the most relevant and high-traffic pages on that website.",
          setup:
            "Configure webhook to receive target_domain, analysis_purpose (outreach/research/competitive), and relevance_criteria (keywords or topics of interest) in the payload.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Ahrefs: Crawl sitemap and fetch top pages by traffic",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://apiv2.ahrefs.com?token={ahrefs_api_key}&from=pages&target={target_domain}&mode=domain&limit=50&order_by=ahrefs_rank&output=json",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              { name: "pages", customName: "top_pages_raw", type: "string" },
              {
                name: "pages[0].url",
                customName: "top_page_1",
                type: "string",
              },
              {
                name: "pages[1].url",
                customName: "top_page_2",
                type: "string",
              },
              {
                name: "pages.length",
                customName: "pages_found",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Rank pages by relevance to criteria and identify decision-maker content",
        520,
        "You are a website intelligence analyst. Rank the fetched pages by relevance to the given criteria. Identify pages likely targeting decision-makers (about, leadership, pricing, case studies). Return JSON with fields: top_relevant_pages (top 10 URLs comma-separated), decision_maker_pages (comma-separated), content_themes (comma-separated), relevance_summary, outreach_angles (comma-separated suggestions), total_relevant.",
        "Domain: {target_domain}, Purpose: {analysis_purpose}, Criteria: {relevance_criteria}, Pages found: {top_pages_raw}",
        [
          {
            field: "top_relevant_pages",
            as: "top_relevant_pages",
            type: "string",
          },
          {
            field: "decision_maker_pages",
            as: "decision_maker_pages",
            type: "string",
          },
          { field: "content_themes", as: "content_themes", type: "string" },
          {
            field: "relevance_summary",
            as: "relevance_summary",
            type: "string",
          },
          { field: "outreach_angles", as: "outreach_angles", type: "string" },
          { field: "total_relevant", as: "total_relevant", type: "number" },
        ],
      ),
      n("4", "runCode", "Filter and format top results with scoring", 760, {
        runCodeDetails: {
          code: "const pages = top_relevant_pages.split(',').map(p => p.trim()); const dmPages = decision_maker_pages.split(',').map(p => p.trim()); const has_dm_pages = dmPages.length > 0 && dmPages[0] !== ''; return { formatted_pages: pages.slice(0, 10).join('\\n'), dm_page_count: dmPages.length, has_dm_pages, result_quality: total_relevant >= 5 ? 'high' : total_relevant >= 2 ? 'moderate' : 'low' };",
          variables: [
            {
              name: "formatted_pages",
              customName: "formatted_pages",
              type: "string",
            },
            {
              name: "dm_page_count",
              customName: "dm_page_count",
              type: "number",
            },
            {
              name: "has_dm_pages",
              customName: "has_dm_pages",
              type: "boolean",
            },
            {
              name: "result_quality",
              customName: "result_quality",
              type: "string",
            },
          ],
        },
      }),
      n("5", "condition", "Decision-maker pages found?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_dm_pages", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send highlighted decision-maker page list",
        1240,
        "{requester_email}",
        "Top Pages Analysis: {target_domain} — {total_relevant} relevant pages found",
        "<h2>Website Page Analysis: {target_domain}</h2><p><strong>Purpose:</strong> {analysis_purpose}</p><p><strong>Relevance Summary:</strong> {relevance_summary}</p><h3>Top Relevant Pages</h3><pre>{formatted_pages}</pre><h3>Decision-Maker Pages ({dm_page_count})</h3><p>{decision_maker_pages}</p><h3>Content Themes</h3><p>{content_themes}</p><h3>Suggested Outreach Angles</h3><p>{outreach_angles}</p><p><em>Result quality: {result_quality} | {pages_found} total pages crawled</em></p>",
      ),
      slackNode(
        "7",
        "Slack: Post page analysis results to research channel",
        1480,
        "🔎 *Top Pages: {target_domain}*\nRelevant pages: {total_relevant} | DM pages: {dm_page_count} | Quality: {result_quality}\n\n*Themes:* {content_themes}\n*Outreach angles:* {outreach_angles}\n\nFull report emailed to requester.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log page analysis results",
        1720,
        "Page_Analysis",
        '[["{timestamp}","{target_domain}","{analysis_purpose}","{total_relevant}","{dm_page_count}","{content_themes}","{result_quality}","{outreach_angles}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Crawls any website via Ahrefs to find top pages, then uses AI to rank them by relevance and identify decision-maker content (leadership, pricing, case studies). Delivers a formatted analysis with outreach angles via email and Slack. Ideal for sales research, competitive analysis, and ABM targeting.",
  },

  // ── 304. Sales Call Coaching Summary | Data/AI | Pattern L ──
  "Sales Call Coaching Summary": {
    nodes: [
      trigger("Gong: Sales call recording completed", "webhook_app", {
        source: "gong",
        event: "call_completed",
        howItStarts:
          "Fires when a sales call recording is completed and processed in Gong, ready for AI coaching analysis.",
        setup:
          "Connect Gong webhook for 'call_completed' events. The payload includes call_id, rep_name, rep_email, prospect_company, call_duration_min, and transcript_url.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze talk ratio, question quality, and sales methodology adherence",
        280,
        "You are an elite sales call coach. Analyze the call transcript for talk-to-listen ratio, question quality, discovery depth, objection handling, and next-step commitment. Return JSON with fields: rep_name, talk_ratio_pct (rep talk percentage), questions_asked (count), discovery_depth (shallow/moderate/deep), objection_handling_score (1-10), next_steps_secured (true/false), top_strength, top_improvement, coaching_point_1, coaching_point_2, coaching_point_3, overall_score (1-100).",
        "Call transcript from Gong: {transcript_url}, Rep: {rep_name}, Duration: {call_duration_min}min, Prospect: {prospect_company}",
        [
          { field: "talk_ratio_pct", as: "talk_ratio_pct", type: "number" },
          { field: "questions_asked", as: "questions_asked", type: "number" },
          { field: "discovery_depth", as: "discovery_depth", type: "string" },
          {
            field: "objection_handling_score",
            as: "objection_handling_score",
            type: "number",
          },
          {
            field: "next_steps_secured",
            as: "next_steps_secured",
            type: "boolean",
          },
          { field: "top_strength", as: "top_strength", type: "string" },
          { field: "top_improvement", as: "top_improvement", type: "string" },
          { field: "coaching_point_1", as: "coaching_point_1", type: "string" },
          { field: "coaching_point_2", as: "coaching_point_2", type: "string" },
          { field: "coaching_point_3", as: "coaching_point_3", type: "string" },
          { field: "overall_score", as: "overall_score", type: "number" },
        ],
      ),
      aiNode(
        "3",
        "AI: Generate targeted coaching recommendations based on call patterns",
        520,
        "You are a sales coaching mentor. Based on the call analysis, provide specific and actionable coaching guidance. Return JSON with fields: primary_recommendation, practice_drill, resource_link_suggestion, benchmark_comparison (above/at/below average), improvement_potential (high/medium/low), manager_note.",
        "Rep: {rep_name}, Score: {overall_score}, Talk ratio: {talk_ratio_pct}%, Questions: {questions_asked}, Discovery: {discovery_depth}, Objection score: {objection_handling_score}/10, Next steps: {next_steps_secured}, Strength: {top_strength}, Improvement: {top_improvement}",
        [
          {
            field: "primary_recommendation",
            as: "primary_recommendation",
            type: "string",
          },
          { field: "practice_drill", as: "practice_drill", type: "string" },
          {
            field: "benchmark_comparison",
            as: "benchmark_comparison",
            type: "string",
          },
          {
            field: "improvement_potential",
            as: "improvement_potential",
            type: "string",
          },
          { field: "manager_note", as: "manager_note", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Score against team benchmarks and determine coaching urgency",
        760,
        {
          runCodeDetails: {
            code: "const needs_coaching = overall_score < 65 || talk_ratio_pct > 70 || objection_handling_score < 5; const urgency = overall_score < 40 ? 'urgent' : overall_score < 65 ? 'recommended' : 'optional'; const talk_ratio_flag = talk_ratio_pct > 65 ? 'Too much talking — aim for 40-60%' : talk_ratio_pct < 30 ? 'Too passive — engage more' : 'Good balance'; return { needs_coaching, urgency, talk_ratio_flag };",
            variables: [
              {
                name: "needs_coaching",
                customName: "needs_coaching",
                type: "boolean",
              },
              {
                name: "urgency",
                customName: "coaching_urgency",
                type: "string",
              },
              {
                name: "talk_ratio_flag",
                customName: "talk_ratio_flag",
                type: "string",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Rep needs coaching intervention?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "needs_coaching", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: DM sales manager with coaching alert and context",
        1240,
        "🎯 *Coaching Alert: {rep_name}* — Score: {overall_score}/100 ({coaching_urgency})\nCall with {prospect_company} ({call_duration_min}min)\n\nTalk ratio: {talk_ratio_pct}% ({talk_ratio_flag})\nDiscovery: {discovery_depth} | Objections: {objection_handling_score}/10\n\n*Manager note:* {manager_note}\n*Recommendation:* {primary_recommendation}\n*Practice drill:* {practice_drill}",
      ),
      emailNode(
        "7",
        "Email: Send coaching summary to rep",
        1480,
        "{rep_email}",
        "Call Coaching: {prospect_company} — Score {overall_score}/100",
        "<h2>Call Coaching Summary</h2><p><strong>Call:</strong> {prospect_company} | {call_duration_min} minutes</p><p><strong>Score:</strong> {overall_score}/100 ({benchmark_comparison} average)</p><h3>Key Metrics</h3><ul><li>Talk ratio: {talk_ratio_pct}% — {talk_ratio_flag}</li><li>Questions asked: {questions_asked}</li><li>Discovery depth: {discovery_depth}</li><li>Objection handling: {objection_handling_score}/10</li><li>Next steps secured: {next_steps_secured}</li></ul><h3>Coaching Points</h3><ol><li>{coaching_point_1}</li><li>{coaching_point_2}</li><li>{coaching_point_3}</li></ol><p><strong>Top Strength:</strong> {top_strength}</p><p><strong>Area to Improve:</strong> {top_improvement}</p><p><strong>Practice Drill:</strong> {practice_drill}</p>",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log call coaching trend data",
        1720,
        "Call_Coaching",
        '[["{timestamp}","{rep_name}","{prospect_company}","{overall_score}","{talk_ratio_pct}","{questions_asked}","{discovery_depth}","{objection_handling_score}","{coaching_urgency}","{benchmark_comparison}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Analyzes completed Gong sales call recordings using AI to evaluate talk ratio, discovery depth, objection handling, and next-step commitment. Scores the call against benchmarks and generates specific coaching recommendations. Alerts managers when coaching intervention is needed and sends reps a detailed self-improvement summary. Trends are tracked in Google Sheets.",
  },

  // ── 305. AI-Summarized Newsletter / RSS Digest | Data/AI | Pattern I ──
  "AI-Summarized Newsletter / RSS Digest": {
    nodes: [
      trigger("Schedule: Daily morning RSS feed collection", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily at 7 AM to fetch new articles from configured RSS feeds, summarize them with AI, and deliver a personalized digest.",
        setup:
          "Configure RSS feed URLs in the workflow settings. Feeds can include industry blogs, news sites, competitor blogs, and thought leadership sources. Set recipient email and Slack channel for digest delivery.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "RSS: Fetch new articles from configured feed sources",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.rss2json.com/v1/api.json?rss_url={rss_feed_url}&api_key={rss2json_key}&count=20",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              { name: "items", customName: "articles_raw", type: "string" },
              {
                name: "items.length",
                customName: "article_count",
                type: "number",
              },
              { name: "feed.title", customName: "feed_title", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Summarize each article and rank by relevance",
        520,
        "You are a content curator and summarizer. For each article, create a concise 2-sentence summary. Rank all articles by relevance and newsworthiness. Return JSON with fields: top_articles (top 5 as JSON array with title, url, summary), total_processed, top_theme, secondary_theme, digest_headline, relevance_distribution (e.g. '3 high, 2 medium').",
        "Articles from {feed_title}: {articles_raw}",
        [
          { field: "top_articles", as: "top_articles", type: "string" },
          { field: "total_processed", as: "total_processed", type: "number" },
          { field: "top_theme", as: "top_theme", type: "string" },
          { field: "secondary_theme", as: "secondary_theme", type: "string" },
          { field: "digest_headline", as: "digest_headline", type: "string" },
          {
            field: "relevance_distribution",
            as: "relevance_distribution",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "runCode",
        "Rank by relevance and format digest with sections",
        760,
        {
          runCodeDetails: {
            code: "const articles = typeof top_articles === 'string' ? JSON.parse(top_articles) : top_articles; const formatted = articles.map((a, i) => `${i+1}. ${a.title}\\n   ${a.summary}\\n   ${a.url}`).join('\\n\\n'); const has_content = articles.length > 0; return { formatted_digest: formatted, has_content, digest_count: articles.length };",
            variables: [
              {
                name: "formatted_digest",
                customName: "formatted_digest",
                type: "string",
              },
              {
                name: "has_content",
                customName: "has_content",
                type: "boolean",
              },
              {
                name: "digest_count",
                customName: "digest_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "AI: Compile personalized digest narrative with key takeaways",
        1000,
        "You are a newsletter editor. Compile the top articles into a cohesive morning digest with a brief editorial intro and key takeaways. Return JSON with fields: editorial_intro, key_takeaway_1, key_takeaway_2, key_takeaway_3, reading_time_min, action_item.",
        "Digest headline: {digest_headline}, Themes: {top_theme}, {secondary_theme}, Articles: {formatted_digest}",
        [
          { field: "editorial_intro", as: "editorial_intro", type: "string" },
          { field: "key_takeaway_1", as: "key_takeaway_1", type: "string" },
          { field: "key_takeaway_2", as: "key_takeaway_2", type: "string" },
          { field: "key_takeaway_3", as: "key_takeaway_3", type: "string" },
          { field: "reading_time_min", as: "reading_time_min", type: "number" },
          { field: "action_item", as: "action_item", type: "string" },
        ],
      ),
      emailNode(
        "6",
        "Email: Send personalized morning digest",
        1240,
        "{digest_recipient_email}",
        "{digest_headline} — {digest_count} articles ({reading_time_min} min read)",
        "<h2>{digest_headline}</h2><p>{editorial_intro}</p><h3>Key Takeaways</h3><ul><li>{key_takeaway_1}</li><li>{key_takeaway_2}</li><li>{key_takeaway_3}</li></ul><h3>Top Articles</h3><pre>{formatted_digest}</pre><p><strong>Themes:</strong> {top_theme}, {secondary_theme}</p><p><strong>Action Item:</strong> {action_item}</p>",
      ),
      slackNode(
        "7",
        "Slack: Post digest highlights to team channel",
        1480,
        "📰 *{digest_headline}*\n{editorial_intro}\n\n*Key Takeaways:*\n• {key_takeaway_1}\n• {key_takeaway_2}\n• {key_takeaway_3}\n\n{digest_count} articles | {reading_time_min} min read | Themes: {top_theme}, {secondary_theme}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log daily digest metrics",
        1720,
        "Digest_Log",
        '[["{timestamp}","{feed_title}","{article_count}","{digest_count}","{top_theme}","{secondary_theme}","{relevance_distribution}","{reading_time_min}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Fetches new articles from RSS feeds daily, uses AI to summarize and rank by relevance, then compiles a personalized digest with editorial intro and key takeaways. Delivers via email and Slack for the morning reading routine. Tracks digest metrics over time in Google Sheets for content curation optimization.",
  },
};
